import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CameraOff } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText, unwrap } from '../../../api/client';
import type { AssetDetail } from '../../../api/types';
import { AssetLink } from '../../../components/assets/AssetLink';
import { formatSpecValue } from '../../../components/assets/SpecInput';
import { AuthImage } from '../../../components/AuthImage';
import { Button, Card, CardBody, CardHeader, DataList, useToast } from '../../../components/ui';
import { formatDate, formatDateTime, formatMoney } from '../../../lib/format';

export const qrUrlFor = (assetNumber: string) => `${window.location.origin}/a/${encodeURIComponent(assetNumber)}`;

function QrCodeImage({ value, size = 128 }: { value: string; size?: number }) {
  const [src, setSrc] = useState<string>();
  useEffect(() => {
    void QRCode.toDataURL(value, { margin: 1, width: size * 2, errorCorrectionLevel: 'M' }).then(setSrc);
  }, [value, size]);
  return src ? <img src={src} alt={`QR-Code ${value}`} width={size} height={size} /> : <div style={{ width: size, height: size }} />;
}

/** Platzhalter ohne Foto: Hinweis „Foto fehlt“ und Schalter „Kein Foto nötig“. */
function NoPhoto({ asset: a, canWrite }: { asset: AssetDetail; canWrite: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const m = useMutation({
    mutationFn: (value: boolean) =>
      unwrap(api.PATCH('/api/assets/{ref}', { params: { path: { ref: a.id } }, body: { version: a.version, photo_not_required: value } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['asset'] });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (e) => toast(errorText(e), 'error'),
  });
  return (
    <div className="flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-t-lg bg-slate-50 text-sm text-slate-400">
      {a.photo_missing ? (
        <span className="inline-flex items-center gap-1.5 text-amber-600">
          <CameraOff className="size-4" /> Foto fehlt
        </span>
      ) : (
        <span>{a.photo_not_required ? 'Kein Foto nötig' : 'Kein Foto'}</span>
      )}
      {canWrite && (a.photo_missing || a.photo_not_required) && (
        <Button size="sm" loading={m.isPending} onClick={() => m.mutate(!a.photo_not_required)}>
          {a.photo_not_required ? 'Doch ein Foto erwarten' : 'Kein Foto nötig'}
        </Button>
      )}
    </div>
  );
}

export function OverviewTab({ asset: a, canWrite = false }: { asset: AssetDetail; canWrite?: boolean }) {
  const emp = a.effective_employee;
  const loc = a.effective_location;
  const inherited = !!a.parent;

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">
        <Card>
          <CardHeader title="Stammdaten" />
          <CardBody>
            <DataList
              items={[
                ['Inventarnummer', <span className="font-mono">{a.asset_number}</span>],
                ['Typ', `${a.asset_type.label}${a.tracking_type === 'component' ? ' (Komponente)' : ''}`],
                ['Hersteller', a.manufacturer],
                ['Modell', a.model],
                ['Seriennummer', a.serial_number && <span className="font-mono">{a.serial_number}</span>],
                ['Service Tag', a.service_tag],
                ['Hersteller-Teilenummer', a.manufacturer_part_number],
                [
                  'Mitarbeiter',
                  emp ? (
                    <Link to={`/employees/${emp.short_code}`} className="text-brand-700 hover:underline">
                      {emp.short_code} – {emp.name}
                      {inherited && <span className="text-slate-400"> (über Gerät)</span>}
                    </Link>
                  ) : null,
                ],
                [
                  'Standort',
                  loc ? (
                    <Link to={`/locations/${loc.id}`} className="text-brand-700 hover:underline">
                      {loc.name}
                      {inherited && <span className="text-slate-400"> (über Gerät)</span>}
                    </Link>
                  ) : null,
                ],
                !!a.parent && ['Eingebaut in', <><AssetLink asset={a.parent} /> {a.parent.name}</>],
                !!a.description && ['Beschreibung', <span className="whitespace-pre-line">{a.description}</span>],
              ]}
            />
          </CardBody>
        </Card>

        {a.specs.length > 0 && (
          <Card>
            <CardHeader title="Technische Daten" />
            <CardBody>
              <DataList items={a.specs.map((s) => [s.label, formatSpecValue(s)])} />
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader title="Kauf und Buchhaltung" />
          <CardBody>
            <DataList
              items={[
                ['Kaufdatum', formatDate(a.purchase_date)],
                ['Inbetriebnahme', formatDate(a.commissioning_date)],
                ['Aktivierungsdatum (AHK)', formatDate(a.capitalization_date)],
                ['Garantie bis', formatDate(a.warranty_until)],
                ['Kosten netto', formatMoney(a.cost_net_cents, a.currency)],
                ['Kosten brutto', formatMoney(a.cost_gross_cents, a.currency)],
                ['Lieferant', a.supplier],
                ['Rechnungsnummer', a.invoice_number],
                ['Bestellnummer', a.order_number],
                a.exclude_from_reconciliation && ['Inventarabgleich', 'ausgenommen (bereits anderweitig abgerechnet)'],
                [
                  'Anlagennummer Buchhaltung',
                  a.accounting_asset_number ??
                    (a.inherited_accounting && (
                      <span>
                        {a.inherited_accounting.accounting_asset_number}{' '}
                        <span className="text-slate-500">
                          (über <AssetLink asset={a.inherited_accounting.via} />)
                        </span>
                      </span>
                    )),
                ],
              ]}
            />
          </CardBody>
        </Card>

        {a.notes && (
          <Card>
            <CardHeader title="Notizen" />
            <CardBody>
              <p className="text-sm whitespace-pre-line text-slate-700">{a.notes}</p>
            </CardBody>
          </Card>
        )}
      </div>

      <div className="space-y-5">
        <Card>
          {a.primary_photo ? (
            <AuthImage src={a.primary_photo.content_url} alt={`Foto ${a.name}`} className="aspect-[4/3] w-full rounded-t-lg" />
          ) : (
            <NoPhoto asset={a} canWrite={canWrite} />
          )}
          <CardBody className="flex items-center gap-4">
            <QrCodeImage value={qrUrlFor(a.asset_number)} size={96} />
            <div className="text-xs text-slate-500">
              QR-Code führt direkt zu diesem Asset.
              <div className="mt-1 font-mono text-slate-700">{a.asset_number}</div>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-xs text-slate-500">
            <div>Angelegt: {formatDateTime(a.created_at)}</div>
            <div>Geändert: {formatDateTime(a.updated_at)}</div>
            <div>Version: {a.version}</div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export { QrCodeImage };
